import { useCallback, useState } from 'react';
import {
  serviceFundMapping,
  useDeletFundFile,
  useSetPasswordToFile
} from '../services/funds.service';
import toast from 'react-hot-toast';

interface ProtectedFile {
  id: string;
  name: string;
  is_password_protected: boolean;
  has_password: boolean;
  [key: string]: unknown;
}

interface Fund {
  bulk_uploaded_files?: ProtectedFile[];
  files_as_per_input?: ProtectedFile[];
  [key: string]: unknown;
}

interface UseDetectProtectedFilesParams {
  refetch?: () => void;
  periodId: string;
}

interface UseDetectProtectedFilesReturn {
  isModalOpen: boolean;
  fileName: string | undefined;
  protectedFiles: ProtectedFile[];
  isDeletingAllProtectedFiles: boolean;
  onSkipFile: () => void;
  onUnblockSkipFile: (pwd: string) => void;
  handleProtectedFiles: (funds: Fund[]) => void;
  handleProtectedPerFiles: (files: ProtectedFile[]) => void;
  onSkipAllFiles: () => void;
  currentFileNumber: number;
  totalFiles: number;
  isLoading: boolean;
  isSkipping: boolean;
}

export default function useDetectProtectedFiles({
  refetch,
  periodId
}: UseDetectProtectedFilesParams): UseDetectProtectedFilesReturn {
  const [protectedFiles, setProtectedFiles] = useState<ProtectedFile[]>([]);
  const [protectedFileNumber, setProtectedFileNumber] = useState<number>(0);
  const [isDeletingAllProtectedFiles, setIsDeletingAllProtectedFiles] = useState<boolean>(false);
  const [isSkipPasswordModalOpen, setIsSkipPasswordModalOpen] = useState<boolean>(false);

  const deleteFundFile = useDeletFundFile();
  const setPasswordToFile = useSetPasswordToFile();

  const handleProtectedFiles = useCallback(
    (funds: Fund[]) => {
      if (!funds || protectedFiles.length > 0) return;

      const bulkUploads = funds[0]?.bulk_uploaded_files || [];
      const individualUploads = funds.map(el => el.files_as_per_input || []).flat();

      const _protectedFiles = ([...bulkUploads, ...individualUploads] as ProtectedFile[]).filter(
        _file => _file.is_password_protected && !_file.has_password
      );

      if (_protectedFiles.length > 0) {
        setProtectedFileNumber(0);
        setIsSkipPasswordModalOpen(true);
      }

      setProtectedFiles(_protectedFiles);
    },
    [protectedFiles, refetch]
  );

  const handleProtectedPerFiles = useCallback(
    (files: ProtectedFile[]) => {
      if (!files || protectedFiles.length > 0) return;

      const _protectedFiles = files.filter(
        _file => _file.is_password_protected && !_file.has_password
      );

      if (_protectedFiles.length > 0) {
        setProtectedFileNumber(0);
        setIsSkipPasswordModalOpen(true);
      }

      setProtectedFiles(_protectedFiles);
    },
    [protectedFiles]
  );

  const clear = useCallback(() => {
    refetch?.();
    setIsSkipPasswordModalOpen(false);
    setProtectedFiles([]);
    setProtectedFileNumber(0);
  }, []);

  const postSkipFiles = useCallback(() => {
    if (protectedFileNumber < protectedFiles.length - 1) {
      setProtectedFileNumber(prev => prev + 1);
    } else clear();
  }, [protectedFiles, protectedFileNumber]);

  const onSkipAllFiles = useCallback(() => {
    setIsDeletingAllProtectedFiles(true);
    Promise.all(
      protectedFiles.map(file => serviceFundMapping.deleteFundFile({ fileId: file.id, periodId }))
    )
      .then(() => {
        clear();
      })
      .catch((err: Record<string, unknown>) => {
        toast.error(
          ((err?.detail as Record<string, unknown>)?.message as string) || 'Error on deleting file'
        );
      })
      .finally(() => {
        setIsDeletingAllProtectedFiles(false);
      });
  }, [protectedFiles, protectedFileNumber, periodId, postSkipFiles, deleteFundFile.mutate]);

  const onSkipFile = useCallback(() => {
    const currentFile = protectedFiles[protectedFileNumber];

    if (!currentFile) return;

    deleteFundFile.mutate(
      {
        fileId: currentFile.id,
        periodId
      },
      {
        onSuccess: () => {
          postSkipFiles();
        },
        onError: (err: Record<string, unknown>) => {
          toast.error(
            `Error on deleting file ${(err?.detail as Record<string, unknown>)?.message}`
          );
        }
      }
    );
  }, [protectedFiles, protectedFileNumber, periodId, postSkipFiles, deleteFundFile.mutate]);

  const onUnblockSkipFile = useCallback(
    (pwd: string) => {
      const currentFile = protectedFiles[protectedFileNumber];

      if (!currentFile) return;

      setPasswordToFile.mutate(
        {
          periodId,
          fileId: currentFile.id,
          data: {
            password: pwd
          }
        },
        {
          onSuccess: () => {
            refetch?.();
            toast.success('Successfully unlocked');
            postSkipFiles();
          },
          onError: (err: Record<string, unknown>) => {
            toast.error(
              `Set password to file error ${(err?.data as Record<string, unknown>)?.detail}`
            );
          }
        }
      );
    },
    [
      protectedFiles,
      protectedFileNumber,
      periodId,
      postSkipFiles,
      setPasswordToFile.mutate,
      refetch
    ]
  );

  return {
    isModalOpen: isSkipPasswordModalOpen,
    fileName: protectedFiles[protectedFileNumber]?.name,
    protectedFiles,
    isDeletingAllProtectedFiles,
    onSkipFile,
    onUnblockSkipFile,
    handleProtectedFiles,
    handleProtectedPerFiles,
    onSkipAllFiles,
    currentFileNumber: protectedFileNumber + 1,
    totalFiles: protectedFiles.length,
    isLoading: setPasswordToFile.isLoading,
    isSkipping: deleteFundFile.isLoading
  };
}
